import { useState, useEffect } from 'react';
import { useAuth } from '../../context/AuthContext';
import { OwnerLayout } from '../../components/layouts/OwnerLayout';
import { BarChart3, Download, FileSpreadsheet, FileText, Users, Briefcase, Package, Calendar, ChevronLeft, ChevronRight } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { toast } from 'sonner';

const MonthlySummary = () => {
  const { api } = useAuth();
  const [loading, setLoading] = useState(true);
  const [summary, setSummary] = useState(null);
  const [materials, setMaterials] = useState(null);
  const [month, setMonth] = useState(new Date().getMonth() + 1);
  const [year, setYear] = useState(new Date().getFullYear());
  const [exporting, setExporting] = useState(null);

  useEffect(() => {
    fetchData();
  }, [month, year]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [summaryRes, materialsRes] = await Promise.all([
        api.get(`/reports/monthly-summary?month=${month}&year=${year}`),
        api.get(`/reports/materials-summary?month=${month}&year=${year}`)
      ]);
      setSummary(summaryRes.data);
      setMaterials(materialsRes.data);
    } catch (error) {
      toast.error('Error al cargar datos');
    } finally {
      setLoading(false);
    }
  };

  const handleExport = async (type) => {
    setExporting(type);
    try {
      let endpoint = '';
      let filename = '';
      let mimeType = '';
      
      if (type === 'reports-excel') {
        endpoint = `/export/reports/excel?month=${month}&year=${year}`;
        filename = `partes_${month}_${year}.xlsx`;
        mimeType = 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet';
      } else if (type === 'materials-excel') {
        endpoint = `/export/materials/excel?month=${month}&year=${year}`;
        filename = `materiales_${month}_${year}.xlsx`;
        mimeType = 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet';
      } else if (type === 'summary-pdf') {
        endpoint = `/export/monthly-summary/pdf?month=${month}&year=${year}`;
        filename = `resumen_${month}_${year}.pdf`;
        mimeType = 'application/pdf';
      } else {
        return;
      }
      
      const response = await api.get(endpoint, { responseType: 'blob' });
      const blob = new Blob([response.data], { type: mimeType });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
      toast.success('Archivo descargado');
    } catch (error) {
      toast.error('Error al exportar');
    } finally {
      setExporting(null);
    }
  };

  const monthNames = ['Enero', 'Febrero', 'Marzo', 'Abril', 'Mayo', 'Junio', 
                      'Julio', 'Agosto', 'Septiembre', 'Octubre', 'Noviembre', 'Diciembre'];

  const prevMonth = () => {
    if (month === 1) {
      setMonth(12);
      setYear(year - 1);
    } else {
      setMonth(month - 1);
    }
  };

  const nextMonth = () => {
    if (month === 12) {
      setMonth(1);
      setYear(year + 1);
    } else {
      setMonth(month + 1);
    }
  };

  return (
    <OwnerLayout>
      <div className="space-y-6" data-testid="monthly-summary-page">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-slate-900">Resumen Mensual</h1>
            <p className="text-slate-500">Estadísticas y exportaciones</p>
          </div>
          <div className="flex items-center gap-2">
            <Button variant="ghost" size="icon" onClick={prevMonth} data-testid="prev-month-btn">
              <ChevronLeft className="w-5 h-5" />
            </Button>
            <span className="font-medium min-w-[150px] text-center">
              {monthNames[month - 1]} {year}
            </span>
            <Button variant="ghost" size="icon" onClick={nextMonth} data-testid="next-month-btn">
              <ChevronRight className="w-5 h-5" />
            </Button>
          </div>
        </div>

        {loading ? (
          <div className="flex justify-center py-12">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-sky-600"></div>
          </div>
        ) : (
          <>
            {/* Stats Grid */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <Card>
                <CardContent className="pt-6">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 bg-sky-100 rounded-lg flex items-center justify-center">
                      <FileText className="w-5 h-5 text-sky-600" />
                    </div>
                    <div>
                      <p className="text-2xl font-bold">{summary?.total_reports || 0}</p>
                      <p className="text-sm text-slate-500">Partes realizados</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
              
              <Card>
                <CardContent className="pt-6">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 bg-green-100 rounded-lg flex items-center justify-center">
                      <Briefcase className="w-5 h-5 text-green-600" />
                    </div>
                    <div>
                      <p className="text-2xl font-bold">{summary?.completion_rate || 0}%</p>
                      <p className="text-sm text-slate-500">Tasa completación</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
              
              <Card>
                <CardContent className="pt-6">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 bg-purple-100 rounded-lg flex items-center justify-center">
                      <Users className="w-5 h-5 text-purple-600" />
                    </div>
                    <div>
                      <p className="text-2xl font-bold">{summary?.clients_serviced || 0}</p>
                      <p className="text-sm text-slate-500">Clientes atendidos</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
              
              <Card>
                <CardContent className="pt-6">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 bg-amber-100 rounded-lg flex items-center justify-center">
                      <Calendar className="w-5 h-5 text-amber-600" />
                    </div>
                    <div>
                      <p className="text-2xl font-bold">{summary?.total_jobs || 0}</p>
                      <p className="text-sm text-slate-500">Trabajos programados</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* Worker Performance */}
            {summary?.worker_stats?.length > 0 && (
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <BarChart3 className="w-5 h-5" />
                    Rendimiento por Trabajador
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-3">
                    {summary.worker_stats.map((worker, idx) => (
                      <div key={idx} className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
                        <span className="font-medium">{worker.name}</span>
                        <span className="text-sky-600 font-bold">{worker.reports_count} partes</span>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Materials Summary */}
            {materials?.materials?.length > 0 && (
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Package className="w-5 h-5" />
                    Consumo de Materiales
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="overflow-x-auto">
                    <table className="w-full">
                      <thead>
                        <tr className="border-b">
                          <th className="text-left py-2 font-medium">Material</th>
                          <th className="text-right py-2 font-medium">Usado</th>
                          <th className="text-right py-2 font-medium">Stock actual</th>
                        </tr>
                      </thead>
                      <tbody>
                        {materials.materials.map((mat, idx) => (
                          <tr key={idx} className="border-b last:border-0">
                            <td className="py-3">{mat.name}</td>
                            <td className="py-3 text-right">{mat.total_quantity} {mat.unit}</td>
                            <td className="py-3 text-right">
                              <span className={mat.current_stock < 10 ? 'text-red-600 font-medium' : ''}>
                                {mat.current_stock || 0}
                              </span>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Export Buttons */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Download className="w-5 h-5" />
                  Exportar Datos
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <Button
                    variant="outline"
                    className="h-auto py-4 flex-col gap-2"
                    onClick={() => handleExport('summary-pdf')}
                    disabled={exporting === 'summary-pdf'}
                    data-testid="export-summary-pdf"
                  >
                    <FileText className="w-6 h-6 text-red-500" />
                    <span>Resumen PDF</span>
                  </Button>
                  
                  <Button
                    variant="outline"
                    className="h-auto py-4 flex-col gap-2"
                    onClick={() => handleExport('reports-excel')}
                    disabled={exporting === 'reports-excel'}
                    data-testid="export-reports-excel"
                  >
                    <FileSpreadsheet className="w-6 h-6 text-green-500" />
                    <span>Partes Excel</span>
                  </Button>
                  
                  <Button
                    variant="outline"
                    className="h-auto py-4 flex-col gap-2"
                    onClick={() => handleExport('materials-excel')}
                    disabled={exporting === 'materials-excel'}
                    data-testid="export-materials-excel"
                  >
                    <FileSpreadsheet className="w-6 h-6 text-green-500" />
                    <span>Materiales Excel</span>
                  </Button>
                </div>
              </CardContent>
            </Card>
          </>
        )}
      </div>
    </OwnerLayout>
  );
};

export default MonthlySummary;
